<!-- A control for the quantity of a line item in a plan -->
<template>
	<div class="w-full">
		<div
			v-if="plan && product"
			class="flex w-full items-center justify-between rounded-lg border-2 bg-white p-3 shadow-md"
			:class="
				cartItem !== undefined ? 'border-accent-500' : 'border-transparent'
			"
		>
			<div>
				<input
					type="checkbox"
					class="text-accent-400 focus:ring-accent-400 h-5 w-5 cursor-pointer rounded-sm border border-solid border-gray-300"
					:checked="cartItem !== undefined"
					@change="toggleSelect"
				/>
				<!-- Name -->
				<span class="text-secondary-900 ml-3 text-sm">
					{{ plan.firstName }} {{ plan.lastName }}
				</span>
			</div>
			<!-- Counter -->
			<div
				v-if="hasCounter"
				class="flex items-center justify-between"
			>
				<button
					type="button"
					class="text-secondary-400 px-2 py-1 text-3xl"
					:disabled="cartItem === undefined || getCurrentQuantity() === 0"
					@click="updateCount(-1)"
				>
					&minus;
				</button>
				<FormKit
					type="number"
					number="integer"
					min="0"
					outer-class="mb-0!"
					inner-class="py-1! px-1! border-slate-100 shadow-none!"
					:model-value="getCurrentQuantity()"
					input-class="text-secondary-900 h-9 w-12 appearance-none rounded-md border border-slate-200 bg-white text-center text-sm font-semibold outline-none focus:border-accent-400 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
					@input="updateQuantity($event as number)"
				/>
				<button
					type="button"
					class="text-secondary-400 px-2 py-1 text-3xl"
					@click="updateCount(1)"
				>
					+
				</button>
			</div>
			<!-- Price -->
			<div
				v-else
				class="text-primary-700 flex w-1/3 items-center justify-end text-sm font-semibold"
			>
				<PriceDetailsDisplay :price-details="priceDetails" />
			</div>
		</div>
		<div v-else>{{ t('merchandise.missingPlanOrProduct') }}</div>
	</div>
</template>

<script setup lang="ts">
import PriceDetailsDisplay from './PriceDetailsDisplay.vue'
import type { PbLineItem } from '@/apiTypes'
import type Plan from '@/classes/Plan'
import { PbProductType } from '@/enums/PbProductType'
import { usePresentationLocale } from '@/i18n'
import { useMerchandise } from '@/mixins/merchandise'
import { computed, type PropType } from 'vue'

const { t } = usePresentationLocale()

const props = defineProps({
	plan: Object as PropType<Plan>,
	product: Object as PropType<PbLineItem>,
	hasCounter: Boolean,
	includeEverplans: {
		type: Boolean,
		default: false,
	},
})

const {
	findLineItemInPlan,
	getPlanLineItemPrice,
	priceBook,
	mockProductEverplans,
} = useMerchandise()

/** Toggle the presence of the line item in the plan */
const toggleSelect = () => {
	if (!props.plan || !props.product) return

	if (props.product.category === PbProductType.Package) {
		const pkg = priceBook.value?.packages.find(
			(p) => p.id === props.product?.id
		)
		if (!pkg) return

		const subItems = priceBook.value?.getProductsInPackage(pkg.id)
		if (!subItems) throw new Error('Package sub-items not found')

		props.plan.togglePackage(pkg, subItems, priceBook.value?.packages ?? [])
	} else if (cartItem.value === undefined) {
		props.plan.addCartProduct(props.product.id)
	} else {
		props.plan.delete(cartItem.value)
	}
}

/** Return the cart item's quantity, falling back to zero for missing or invalid values */
const getCurrentQuantity = () => cartItem.value?.quantity || 0

/** Increment or decrement the quantity of the line item in the plan */
const updateCount = (inc: number) => {
	props.plan!.addCartProduct(props.product!.id, inc + getCurrentQuantity())
}

/** Update the cart item count based on the quantity typed into the input */
const updateQuantity = (quantity: number | undefined) => {
	const change = (quantity ?? 0) - getCurrentQuantity()

	if (change !== 0) updateCount(change)
}

/** The cartItem representing the line item in the plan */
const cartItem = computed(() => {
	if (!props.product) return undefined
	return findLineItemInPlan(props.product as PbLineItem, props.plan as Plan)
})

const priceDetails = computed(() => {
	if (!props.plan || !props.product) return undefined

	const basePrice = getPlanLineItemPrice(props.plan, props.product.id)

	if (props.includeEverplans && mockProductEverplans.value) {
		const everplansPrice = mockProductEverplans.value.fixedPrice ?? 0
		return {
			...basePrice,
			price: (basePrice?.price ?? 0) + everplansPrice,
		}
	}

	return basePrice
})
</script>
